import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Box, Button, CardActions, CardContent, Grid, Paper, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useFRCallback } from '@/hooks';
import { getHiddenValueCallbackById } from '@/global';

const NearExpiredPassword = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const { callbacks = [], confirmationCallbackIndex, confirmationCallbackOptions } = useFRCallback(stepToRender);

  // get expiration date from callbacks
  const expirationDate = useMemo(() => {
    // loop through callbacks to find the callback with the expiration date
    const expirationDateCallback = getHiddenValueCallbackById(callbacks, 'expirationdateinseconds');

    // take the stored callback and extract the value
    const { output: expDateOutput } = expirationDateCallback;
    const expDateValue = find(expDateOutput, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;
    const parsedValue = JSON.parse(expDateValue);

    // convert the string to a date
    const unformattedDate = new Date(parsedValue * 1000);

    // format should be - Weekday Month dd, yyyy
    const output = moment(unformattedDate).format('dddd, MMMM Do YYYY');

    return output;
  }, [callbacks]);

  // get days until expiration from callbacks
  const daysUntilExpiration = useMemo(() => {
    // loop through callbacks to find the callback with the days until expiration
    const daysUntilExpirationCallback = getHiddenValueCallbackById(callbacks, 'daysuntilexpiration');

    // take the stored callback and extract the value
    const { output: daysUntilExpOutput } = daysUntilExpirationCallback;
    const expDateValue = find(daysUntilExpOutput, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;

    // need to ensure the value is converted into an integer format; can then output to string
    const formattedValue = Number.parseInt(expDateValue, 10);

    return formattedValue?.toString();
  }, [callbacks]);

  const handleNavigation = (selectedNavigation) => {
    // NOTE: options in callback should be 'Yes' or 'No'
    const output = stepToRender;

    // find the index of the desired navigation
    const newCallbacks = [...callbacks];
    let indexToUse;
    if (selectedNavigation === 'yes') {
      indexToUse = findIndex(confirmationCallbackOptions, (optionValue) => optionValue?.toLowerCase() === 'yes');
    } else if (selectedNavigation === 'no') {
      indexToUse = findIndex(confirmationCallbackOptions, (optionValue) => optionValue?.toLowerCase() === 'no');
    }

    if (indexToUse) {
      // build new callbacks with the desired navigation option index in the input
      newCallbacks[confirmationCallbackIndex].input = [
        {
          ...newCallbacks[confirmationCallbackIndex].input[0],
          value: indexToUse,
        },
      ];
    }

    Object.assign(output.payload.callbacks, [...newCallbacks]);

    setCurrentStep({ ...output });
  };

  return (
    <Grid
      id="near-expired-password-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '600px' } }}>
            <CardContent>
              <Box mb={3}>
                <Typography variant="h1" mb={2}>
                  {daysUntilExpiration > 0
                    ? `Your password expires in ${daysUntilExpiration} day${daysUntilExpiration > 1 ? 's' : ''}.`
                    : 'Your password expires today.'}
                </Typography>
              </Box>
              <Typography variant="subtitle1">{`It must be changed by ${expirationDate}.`}</Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'end' }}>
              <Button color="secondary" onClick={() => handleNavigation('no')} sx={{ marginBottom: { xs: 1, md: 0 } }}>
                I&apos;ll change my password next time
              </Button>
              <Button color="primary" onClick={() => handleNavigation('yes')} sx={{ marginLeft: 1 }}>
                Change my password
              </Button>
            </CardActions>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

NearExpiredPassword.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default NearExpiredPassword;
